import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Row, Col } from 'react-bootstrap'
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify'
import { useDispatch, useStore } from 'react-redux';
import useRazorpay from "react-razorpay";
import { logout } from '../../actions/auth';
import useAxiosInstance from '../../Api/AxiosInstance';
import AddressFormModal from './AddressFormModal';

/* ---------------------------------------------------------------------------
   NEW: Checkout popup for the shop page.
   Opens from the cart panel's "Proceed to checkout" button instead of
   navigating to /checkout. Everything the checkout page does is reproduced here:
   address picker, add/edit address, Self Pickup, Pickup Location, the cart line
   items, the totals, and the Razorpay payment.

   IMPORTANT - this popup submits the payment itself rather than handing off to
   /checkout. The chain is add-order-details -> create-order-id-in-razorpay ->
   Razorpay -> add-payment-details, and running it twice (once here, again after a
   navigation) is how duplicate orders get created. So the four calls are
   deliberately duplicated from Checkout.js rather than shared. Checkout.js is
   untouched and still works on its own.

   Shipping is NOT a fixed value. get-shipping-charges is keyed on
   { address_id, clinic_id, self_pickup } and must be re-fetched whenever the
   address or self-pickup changes, exactly as Checkout.js does. There is no flat
   rate to hardcode - the shippingPrice state starts at 0 until the API answers.
--------------------------------------------------------------------------- */
function CheckoutPopup({ show, onHide, items, sum, gst }) {

  const AxiosInstance = useAxiosInstance()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const logOut = useCallback(() => {
    dispatch(logout());
  }, [dispatch]);
  const { getState } = useStore((state) => state);
  const { user } = getState().auth
  const [razorpay, isLoaded] = useRazorpay();

  // Address state - mirrors Checkout.js.
  const [address, setAddress] = useState([])
  const [address_id, setAddress_id] = useState('')
  const [selectedState, setSelectedState] = useState(null)
  const [selectedAddress, setSelectedAddress] = useState(null)
  const [shippingPrice, setShippingPrice] = useState(0)

  // Gurugram is fixed: the city selector is commented out in the markup below,
  // so clinic_id never changes from 1. Kept as state so the rest of the logic
  // (get-shipping-charges, add-order-details) is identical to Checkout.js.
  const [clinic_id, setSelectedClinic] = useState(1);
  const [self_pickup, setSelfPickup] = useState(null);

  // Nested "Address Details" modal + its supporting dropdown data.
  const [showAddressForm, setShowAddressForm] = useState(false)
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [currentCountry, setCurrentCountry] = useState('101');
  const [refreshAddresses, setRefreshAddresses] = useState(0)

  let User_id = user?.user?.id

  let total = sum + shippingPrice + gst

  const cartIds = [];
  for (let i = 0; i < items.length; i++) {
    cartIds.push(items[i].cart_id);
  }
  let cart_id = cartIds.join(',');

  // Verbatim from Checkout.js.
  const getShippingCharges = async (id) => {
    const response = await AxiosInstance.post('get-shipping-charges', { address_id: id, clinic_id, self_pickup });
    setShippingPrice(response.data.data.amount);
  };

  // Same condition as Checkout.js: only fetch once BOTH address and self_pickup
  // are settled, otherwise shippingPrice stays at its initial 0.
  useEffect(() => {
    if (address_id != "" && self_pickup != null) {
      getShippingCharges(address_id);
    }
  }, [self_pickup, clinic_id, address_id]);

  // Address list. Re-fetches once the user has just saved an address, or when
  // the checkout page's own isFormVisible equivalent flips - here we trigger it
  // by bumping refreshAddresses after a save.
  useEffect(() => {
    const fetchaddress = async () => {
      try {
        const response = await AxiosInstance.post('billing-address')
        setAddress(response.data.data)

        if (response.data.data && response.data.data.length > 0) {
          const firstAddress = response.data.data[0];
          setAddress_id(firstAddress.id);
          setSelectedState(firstAddress.state_id ? String(firstAddress.state_id) : null);
          getShippingCharges(firstAddress.id);
        }
      } catch (error) {

      }
    }
    fetchaddress()
  }, [show, refreshAddresses])

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await AxiosInstance.post('countries');
        setCountries((response?.data?.data?.countries || []).map(c => {
          const raw = c.value ?? c.id ?? c.country_id ?? c.code;
          return { ...c, value: raw != null ? String(raw) : '' };
        }));
      } catch (error) {
        console.error('Error fetching countries:', error);
      }
    };
    fetchCountries();
  }, []);

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await AxiosInstance.post('states', { country: currentCountry });
        setStates((response?.data?.data?.states || []).map(s => {
          const raw = s.value ?? s.id ?? s.state_id;
          return { ...s, value: raw != null ? String(raw) : '' };
        }));
      } catch (error) {
        console.error('Error fetching states:', error);
      }
    };
    if (currentCountry) {
      fetchStates();
    }
  }, [currentCountry]);

  const handleEditAddress = (address) => {
    setSelectedAddress(address);
    setCurrentCountry(String(address.country_id));
    setSelectedState(address.state_id ? String(address.state_id) : null);
    setShowAddressForm(true);
  };

  // Verbatim from Checkout.js handlePayment - same validation, same endpoints,
  // same Razorpay options. Only difference: this closes the popup instead of
  // living on a page.
  const handlePayment = async (amount) => {
    if (!address_id && !self_pickup) {
      toast.error("Please select either an address or choose self pickup.", {
        position: "top-right",
        autoClose: 2000,
      });
      return;
    }
    if (!self_pickup && address_id && selectedState == null) {
      toast.error("Please select valid address with state.", {
        position: "top-right",
        autoClose: 2000,
      });
      return;
    }
    try {
      const orderDetailsResponse = await AxiosInstance.post('add-order-details', { total_amount: amount, sub_total: sum, user_id: User_id, cart_id: cart_id, address_id: address_id, shipping_charge: shippingPrice, self_pickup, clinic_id });

      const checkedorderId = orderDetailsResponse.data.data.id;

      const response = await AxiosInstance.post("create-order-id-in-razorpay", { amount: amount, order_id: checkedorderId });
      if (response.status === 400 && response.message === "Token is expired!!") {
        logOut();
        navigate("/");
      } else {
        if (response.status === 200) {
          const razorpay_key = process.env.REACT_APP_RAZORPAY_KEY_ID;
          const options = {
            key: razorpay_key,
            accept_partial: false,
            amount: Math.round(amount * 100),
            currency: "INR",
            name: "Eugenix",
            description: "Transaction",
            image: "https://lms.eugenixhairsciences.com/assets/front/images/logo.png",
            order_id: response.data.data.order_id,
            handler: async (res) => {
              try {
                if (res.razorpay_payment_id) {
                  const response = await AxiosInstance.post("add-payment-details", { table_id: checkedorderId, type_id: 2, payment_id: res.razorpay_payment_id, amount: amount, shipping_charge: shippingPrice })
                  if (response.data.status == 200) {
                    navigate('/myorder')
                    window.location.reload()
                  }
                }
              } catch (error) {
                console.log(error)
              }
            },
            notes: {
              permanent_address: "Razorpay Corporate Office",
            },
            theme: {
              color: "#3399cc",
            },
            modal: {
              ondismiss: function () {
                onHide();
              }
            }
          };
          const rzpay = new razorpay(options);
          rzpay.open();
        }
      }
    } catch (error) {
      console.log(error);
      const errorData = error.response?.data;
      if (errorData) {
        if (errorData.data?.message && typeof errorData.data.message === 'object') {
          Object.values(errorData.data.message).forEach(msgs => {
            if (Array.isArray(msgs)) {
              msgs.forEach(msg => toast.error(msg));
            } else {
              toast.error(msgs);
            }
          });
        } else if (errorData.message) {
          toast.error(errorData.message);
        } else {
          toast.error('Something went wrong. Please try again.');
        }
      } else {
        toast.error(error.message || 'Something went wrong. Please try again.');
      }
    }
  }

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg" centered>
        <Modal.Body>
          <div className='checkout-form'>
            <div className='box-title'>
              <h3>Select Billing address</h3>
            </div>

            {/* Address radio list - same markup as Checkout.js. */}
            <div className='form-inner d-flex'>
              {address?.map((u, index) =>
                <Form.Group className='input-field full-width relative' key={u.id}>
                  <label className='block-radiobox'>
                    <Form.Control type='radio'
                      name="address"
                      value={u.id}
                      onChange={() => { setAddress_id(u.id); setSelectedState(u.state_id ? String(u.state_id) : null); getShippingCharges(u.id); }}
                      checked={address_id === u.id}
                    />
                    <div className='card-address trans'>
                      <h4>{u.full_name}</h4>
                      <p>{u.current_address} , {u.permanent_address} , {u.location} , {u.pincode}  </p>
                    </div>
                  </label>
                  <button onClick={() => handleEditAddress(u)} className='edit-add-btn' type='button'>
                    <i className='fa fa-pencil'></i>
                  </button>
                </Form.Group>
              )}
            </div>
            <div className='address-new-block ptb-30 pb-0'>
              <span className='btn gray-btn' onClick={() => { setSelectedAddress(null); setCurrentCountry('101'); setShowAddressForm(true); }}>+ Add New Address</span>
            </div>

            <hr className='mt-4' />

            {/* Self Pickup - only applicable for the Gurugram (Delhi NCR)
                location, so the scope is stated on the control itself.

                The logic needs no gating: clinic_id is fixed at 1 (Gurugram)
                below, so this checkbox can only ever relate to Gurugram. It
                mirrors Checkout.js: self_pickup starts null, and
                get-shipping-charges is only called once BOTH address_id and
                self_pickup are set, so ticking this box is what makes the
                shipping figure resolve.
            */}
            <div className='input-field full-width'>
              <div className='term-agree-block d-flex v-center'>
                <label className='checkbox'>
                  <input type='checkbox' onChange={() => { setSelfPickup(!self_pickup); }} checked={!!self_pickup} /><span className='checkmark'></span>Self Pickup <span className='self-pickup-note'>(only applicable in Gurugram location)</span>
                </label>
              </div>
            </div>

            {/* Pickup Location - city selector removed, Gurugram fixed.
                Scope note removed from this label as requested, so the note now
                appears on the Self Pickup checkbox only. PREVIOUS (kept for
                restore, was inside the <label> after "Pickup Location"):
                <span className='self-pickup-note'>(only applicable in Gurugram location)</span>

                Also kept for restore: the <select name="clinic_id"> with
                options 1 GURUGRAM, 2 MUMBAI, 7 BHUBANESWAR, 10 HYDERABAD.
            */}
            <div className='input-field full-width'>
              <label className='label'>Pickup Location </label>
              <div className='pickup-location-fixed'>GURUGRAM (DELHI NCR)</div>
            </div>
            {/*
            <div className='input-field full-width'>
              <label className='label'>Pickup Location </label>
              <select aria-label="Select Pickup Location"
                name="clinic_id"
                onChange={(event) => {
                  setSelectedClinic(event.target.value);
                }}
              >
                <option value="1">GURUGRAM (DELHI NCR)</option>
                <option value="2">MUMBAI</option>
                <option value="7">BHUBANESWAR</option>
                <option value="10">HYDERABAD</option>
              </select>
            </div>
            */}

            <hr className='mt-3' />

            {/* Cart line items. */}
            <div className='box-title'>
              <h4>Your Order</h4>
            </div>
            <div className='checkout-popup-items'>
              {items?.map((item, index) =>
                <div className='checkout-popup-row' key={index}>
                  <div className='shop-cart-thumb'>
                    <img src={item.image} alt='' />
                  </div>
                  <div className='shop-cart-details'>
                    <p className='shop-cart-name' title={item.name}>{item.name}</p>
                    <div className='shop-cart-meta'>
                      <span className='shop-cart-unit'>Qty {item.quantity}</span>
                      <span className='shop-cart-subtotal'>₹ {item.sub_total.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Totals - shipping comes from get-shipping-charges, not a constant. */}
            <div className='box-title text-center mt-3'>
              <h4>Cart Total</h4>
            </div>
            <div className='cart-total-list'>
              <div className='sub-item d-flex'>
                <label className="cart-label">Subtotal</label>
                <span className='cart-value'>₹ {sum.toFixed(2)}</span>
              </div>
              <div className='sub-item d-flex'>
                <label className="cart-label">GST</label>
                <span className='cart-value'>₹ {gst.toFixed(2)}</span>
              </div>
              <div className='sub-item d-flex'>
                <label className="cart-label">Shipping and Handing</label>
                <span className='cart-value'>₹ {shippingPrice.toFixed(2)}</span>
              </div>
              <div className='sub-item d-flex grand-total-item'>
                <label className="cart-label"><strong>Total</strong></label>
                <span className='cart-value'><strong>₹ {total.toFixed(2)}</strong></span>
              </div>
              <div className='sub-item d-flex form-btnbox'>
                <button type='button' className='btn secondary w-100' onClick={() => handlePayment(total)}>Pay Now <i className="las la-shopping-cart ml-2"></i></button>
              </div>
              <div className='sub-item d-flex form-btnbox'>
                <button type='button' className='btn primary w-100' onClick={onHide}>Cancel</button>
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <AddressFormModal
        show={showAddressForm}
        onHide={() => setShowAddressForm(false)}
        selectedAddress={selectedAddress}
        user={user}
        countries={countries}
        states={states}
        currentCountry={currentCountry}
        setCurrentCountry={setCurrentCountry}
        onSaved={() => { setShowAddressForm(false); setRefreshAddresses(refreshAddresses + 1); }}
      />
    </>
  )
}

export default CheckoutPopup
